import React, { useEffect, useMemo, useState } from 'react'
import { useGetCostTrackerQuery, useLazyGetCostTrackerQuery } from 'store/slices/apis/costSlice';
import CostTrackerTable from './CostTrackerTable';
import { Box, Typography, Tooltip, TableContainer, Table, TableBody, TableRow, TableCell, Paper, Grid, Card, CardContent, Stack } from '@mui/material';

const transformApiData = (apiData) => {
    console.log("apiData", apiData);
    return apiData?.items?.map((item) => {
        let flattenedRow = {
            ...item,
            id: item.activityId,
        };

        flattenedRow.children = [];

        // if (item.isLeaf === false) {
        //   flattenedRow.hasChildren = true;
        // }
        flattenedRow.hasChildren = true;
        return flattenedRow;
    });
};

const Header = ({ title, subtitle, align = "center" }) => (
    <Box
        sx={{
            width: "100%",
            textAlign: align,
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.1,
        }}
    >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

const renderVariance = (sv) => {
    if (sv === null || sv === undefined) {
        return "N/A";
    }

    let color = "#6B7280"; // default gray
    let label = "On Schedule";
    let dot = "🟡";

    if (sv < 0) {
        color = "#DC2626"; // red
        label = "Behind Schedule";
        dot = "🔴";
    } else if (sv >= 0) {
        color = "#16A34A"; // green
        label = "Ahead of Schedule";
        dot = "🟢";
    }

    return (
        <Tooltip title={label}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                }}
            >
                <Typography sx={{ fontSize: 14 }}>
                    {dot}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color,
                    }}
                >
                    {sv.toFixed(2)}
                </Typography>
            </Box>
        </Tooltip>
    );
};
const renderPerformanceIndex = (performanceIndex) => {
    if (performanceIndex === null || performanceIndex === undefined) {
        return "N/A";
    }

    let color = "#6B7280"; // default gray
    let label = "";
    let dot = "🟡";

    if (performanceIndex < .95) {
        color = "#DC2626"; // red
        label = "";
        dot = "🔴";
    }
    else if (performanceIndex < 1) {
        color = "#6B7280"; // grey
        label = "";
        dot = "🟡";
    } else if (performanceIndex >= 1) {
        color = "#16A34A"; // green
        label = "";
        dot = "🟢";
    }

    return (
        <Tooltip title={label}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                }}
            >
                <Typography sx={{ fontSize: 14 }}>
                    {dot}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color,
                    }}
                >
                    {performanceIndex.toFixed(2)}
                </Typography>
            </Box>
        </Tooltip>
    );
};
const renderEAC = (eac, bac) => {
    if (eac === null || eac === undefined) {
        return "N/A";
    }

    let color = "#6B7280"; // default gray
    let label = "";
    let dot = "🟡";

    if (eac <= bac) {
        color = "#16A34A"; // red
        label = "";
        dot = "🟢";
    }
    else {
        color = "#DC2626"; // red
        label = "";
        dot = "🔴";
    }

    return (
        <Tooltip title={label}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                }}
            >
                <Typography sx={{ fontSize: 14 }}>
                    {dot}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        color,
                    }}
                >
                    {eac.toFixed(2)}
                </Typography>
            </Box>
        </Tooltip>
    );
};

const abbreviations = [
    { short: "PV", full: "Planned value", color: "#00344C" },
    { short: "AC", full: "Actual cost", color: "#00344C" },
    { short: "BAC", full: "Budget at completion", color: "#00344C" },
    { short: "BCWS", full: "Budgeted cost of work scheduled", color: "#00344C" },
    { short: "EV", full: "Earned value", color: "#00344C" },
    { short: "CV", full: "Cost variance", color: "#00344C" },
    { short: "EAC", full: "Estimated at completion", color: "#00344C" },
    { short: "BCWP", full: "Budgeted cost of work performed", color: "#00344C" },
    { short: "SV", full: "Schedule variance", color: "#00344C" },
    { short: "CPI", full: "Cost performance index", color: "#00344C" },
    { short: "ETC", full: "Estimated cost to complete", color: "#00344C" },
    { short: "ACWP", full: "Actual cost of work performed", color: "#00344C" },
    { short: "SPI", full: "Schedule performance index", color: "#00344C" },

    // { short: "HVAC", full: "Heating Ventilation and Air Conditioning", color: "#2563eb" },
    // { short: "BOQ", full: "Bill of Quantity", color: "#16a34a" },
    // { short: "MEP", full: "Mechanical Electrical Plumbing", color: "#f59e0b" },
    // { short: "BMS", full: "Building Management System", color: "#9333ea" },
    // { short: "ELV", full: "Extra Low Voltage", color: "#ef4444" }
];


const AbbreviationBadge = ({
    short,
    full,
    color = "#2563eb",
    expandable = true
}) => {

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E7ECF3",
                borderRadius: "3px",
                overflow: "hidden",
                display: "flex",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    // cursor: expandable ? "pointer" : "default"
                }}
            >
                <Box
                    sx={{
                        px: 0,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Tooltip title={full} arrow>
                        <Typography sx={{ width: 48, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700, fontSize: 11, color: "#3157C8", backgroundColor: "#EDF4FF", borderRight: "1px solid #D8E0EA" }}>
                            {short}
                        </Typography>
                    </Tooltip>

                    <Typography title={full} sx={{ px: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10.5, color: "#71819A" }}>
                        {full}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}


const CostTrackerContainer = ({ projectId, filters }) => {
    const [initialData, setInitialData] = useState([]);
    const [loaderId, setLoaderId] = useState(null);

    const { data, isFetching, isError } = useGetCostTrackerQuery({
        projectId,
        activityId: filters.activityId,
    });

    const [trigger, { isFetching: lazyFetching, isError: lazyError }] = useLazyGetCostTrackerQuery();


    useEffect(() => {
        if (data?.data) {
            setInitialData(transformApiData(data.data));
        }
    }, [data]);

    const handleRowExpand = async (row) => {
        if (row.children?.length > 0) return;

        try {
            const parentId = row.id;
            setLoaderId(parentId);
            const result = await trigger({
                projectId: projectId,
                parentId: parentId,
            }).unwrap();

            const childrens = transformApiData(result?.data) || [];

            const updatedData = insertChildByParentId(
                initialData,
                parentId,
                childrens
            );
            console.log("updated data", updatedData);
            setInitialData(updatedData);
        } catch (error) {
            console.log("Lazy Load Error:", error);
        } finally {
            setLoaderId(null);
        }
    };

    const insertChildByParentId = (prevData, parentId, newData) => {
        return prevData.map((item) => {
            if (item.id === parentId) {
                return { ...item, children: newData, hasChildren: newData?.length > 0 ? true : false };
            }
            if (item.children?.length > 0) {
                return {
                    ...item,
                    children: insertChildByParentId(item.children, parentId, newData),
                };
            }
            return item;
        });
    };

    const tableColumns = useMemo(() => {
        const cols = [
            {
                name: <Header title="Activity" align="left" />,
                field: "activity",
                sticky: true,
                width: 400,
                align: "left",
                renderer: (row) => (
                    row?.activity ?? "N/A"
                ),
            },
            {
                name: <Header title="BAC" subtitle={"(INR Cr.)"} />,
                field: "bac",
                width: 80,
                renderer: (row) => (
                    row?.bac ?? "N/A"
                ),
            },
            {
                name: <Header title="Committed Cost" subtitle={"(INR Cr.)"} />,
                field: "committedCost",
                width: 80,
                renderer: (row) => (
                    row?.committedCost ?? "N/A"
                ),
            },
            {
                name: <Header title="BCWS (PV)" subtitle={"(INR Cr.)"} />,
                field: "bcws",
                width: 80,
                renderer: (row) => (
                    row?.bcws ?? "N/A"
                ),
            },
            {
                name: <Header title="BCWP (EV)" subtitle={"(INR Cr.)"} />,
                field: "bcwp",
                width: 80,
                renderer: (row) => (
                    row?.bcwp ?? "N/A"
                ),
            },
            {
                name: <Header title="SV (EV-PV)" subtitle={"(INR Cr.)"} />,
                field: "sv",
                width: 80,
                renderer: (row) => (
                    renderVariance(row?.sv) ?? "N/A"
                ),
            },
            {
                name: <Header title="SPI (EV/PV)" />,
                field: "spi",
                width: 80,
                renderer: (row) => (
                    renderPerformanceIndex(row?.spi) ?? "N/A"
                ),
            },
            {
                name: <Header title="ACWP (AC)" subtitle={"(INR Cr.)"} />,
                field: "ac",
                width: 80,
                renderer: (row) => (
                    row?.ac ?? "N/A"
                ),
            },
            {
                name: <Header title="CV (EV-AC)" subtitle={"(INR Cr.)"} />,
                field: "cv",
                width: 80,
                renderer: (row) => (
                    renderVariance(row?.cv) ?? "N/A"
                ),
            },
            {
                name: <Header title="CPI (EV/AC)" />,
                field: "cpi",
                width: 80,
                renderer: (row) => (
                    renderPerformanceIndex(row?.cpi) ?? "N/A"
                ),
            },
            {
                name: <Header title="ETC wrt Cost Committed" subtitle={"(INR Cr.)"} />,
                field: "etC_wrt_Committed",
                width: 80,
                renderer: (row) => (
                    row?.etC_wrt_Committed ?? "N/A"
                ),
            },
            {
                name: <Header title="ETC wrt BAC" subtitle={"(INR Cr.)"} />,
                field: "etC_wrt_BAC",
                width: 80,
                renderer: (row) => (
                    row?.etC_wrt_BAC ?? "N/A"
                ),
            },
            {
                name: <Header title="EAC" subtitle={"(INR Cr.)"} />,
                field: "eac",
                width: 80,
                renderer: (row) => (
                    renderEAC(row?.eac, row?.bac) ?? "N/A"
                ),
            },
            // {
            //   name: "Remarks",
            //   width: 100,
            //   field: "remarks",
            //   renderer: (row) => (
            //     row?.remarks ?? "N/A"
            //   ),
            // },

        ];
        return cols;
    }, []);



    return (
        <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
            <Box sx={{ mb: 1.25, p: 1, border: "1px solid #E7ECF3", backgroundColor: "#FBFCFE" }}>
                <Typography sx={{ mb: 0.75, fontSize: 10, fontWeight: 700, color: "#46566F", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Cost terminology
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 0.6 }}>
                {abbreviations.map((item) => (
                    <AbbreviationBadge key={item.short} {...item} />
                ))}
                </Box>
            </Box>
            <CostTrackerTable
                data={initialData}
                columns={tableColumns}
                onRowExpand={handleRowExpand}
                loaderId={loaderId}
                loading={isFetching || lazyFetching}
                error={isError || lazyError}
            />
        </Box>
    )
}

export default CostTrackerContainer
